// public/v3/src/lib/panel-review.js — pure helpers for AI Panel Review.
//
// No React, no DOM, no fetch. Build de-identified chunks, parse Claude's
// response, compute v2-compatible suggestion hashes. The orchestrator
// (pages/onboard.jsx) wires these to lib/api.js for the actual /ai call.
//
// Module format: CommonJS (module.exports). Jest reads this file directly
// (no babel-jest preset-env installed), and esbuild interops CommonJS
// modules cleanly when consumed via `import { x } from './panel-review'`
// from JSX pages — same way `import React from 'react'` works against the
// CommonJS react package. If a preset-env transformer is later added,
// switch to `export function` to match the rest of public/v3/src/lib.

// _suggestionHash from v2 ccm-compliance.js:4324-4331 — DO NOT change the
// algorithm. The hash is the dedupe key in panel_review_dispositions, so
// v2 and v3 must produce identical output for the same (programFamily, code).
function suggestionHash(programFamily, code) {
  const str = (programFamily || '') + ':' + (code || '');
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return 'sh_' + (h >>> 0).toString(36);
}

const CCM_PROGRAM_CODES = new Set(['99490', '99491', '99487']);

function isCcmProgramCode(code) {
  return CCM_PROGRAM_CODES.has(String(code || '').trim());
}

function normalizeCcmSuggestionForDisposition(suggestion) {
  if (!suggestion || !isCcmProgramCode(suggestion.recommendedCode)) return null;
  const originalProgramFamily = suggestion.programFamily;
  const normalized = Object.assign({}, suggestion, {
    programFamily: 'CCM',
    recommendedCode: String(suggestion.recommendedCode).trim(),
  });
  if (originalProgramFamily && originalProgramFamily !== 'CCM') {
    normalized.originalProgramFamily = originalProgramFamily;
  }
  return normalized;
}

function pickCcmSuggestion(suggestions) {
  if (!Array.isArray(suggestions)) return null;
  for (const suggestion of suggestions) {
    if (suggestion && isCcmProgramCode(suggestion.recommendedCode)) {
      return normalizeCcmSuggestionForDisposition(suggestion);
    }
  }
  return null;
}

// pickBhiSuggestion — given the Onboard AI-suggestion array (the rows Claude
// returns from the de-identified panel-review call), return the first
// BHI-family entry. Mirrors panel-review.js's pickCcmSuggestion shape:
// Array.isArray guard, first-match wins, null on miss. Both case variants
// ('BHI' and 'bhi') and both casing conventions (`programFamily` and the
// snake_case `program_family`) match — Onboard uses the camelCase shape today,
// but Claude's raw JSON occasionally surfaces the snake_case key, and
// downstream callers shouldn't have to normalize.
function pickBhiSuggestion(suggestions) {
  if (!Array.isArray(suggestions)) return null;
  return suggestions.find((s) => {
    const family = String((s && (s.programFamily || s.program_family)) || '').toLowerCase();
    return family === 'bhi';
  }) || null;
}

// Safe-harbor de-identification (port of v2's buildSafeHarborPanelRow,
// ccm-compliance.js:3664-3754, adapted to v3's data shape).
//
// Returns { alias, row, mapEntry } where:
//   alias    — '[PATIENT_NNNN]' string sent to Claude
//   row      — de-identified payload sent to Claude (no name/MRN/DOB)
//   mapEntry — { patientId, displayName, existingPrograms } kept local
//
// Note: v2's full row enriches with BUILTIN_CONDITIONS flags and
// MED_PATTERNS-derived signals. v3 omits the enrichment — Claude can infer
// from condition descriptions and the med list. Re-add if Onboard suggestions
// degrade noticeably in practice.
function buildSafeHarborPanelRow(patient, aliasIndex) {
  const idx = String(aliasIndex).padStart(4, '0');
  const alias = '[PATIENT_' + idx + ']';

  const rawAge = _ageFromDob(patient.dob);
  const safeAge = rawAge === null ? 'unknown' : (rawAge >= 90 ? '90+' : rawAge);

  const billing = patient.billing || {};
  const currentPrograms = [];
  ['program', 'psychProgram', 'painProgram', 'pcmProgram', 'rpmProgram', 'navProgram'].forEach((key) => {
    if (billing[key] && billing[key] !== 'none') currentPrograms.push(billing[key]);
  });

  const problemList = (patient.conditions || []).map((c) => ({
    description: c.description || '',
    icd10: Array.isArray(c.icd10) ? c.icd10 : (c.icd10 ? [c.icd10] : []),
  }));

  const medications = (patient.medications || []).map((m) => {
    const parts = [m.name || ''];
    if (m.strength) parts.push(m.strength);
    if (m.directions) parts.push(m.directions);
    return parts.join(' ').trim();
  }).filter(Boolean);

  const specs = (patient.outsideProviders || [])
    .map((op) => op.specialty || '')
    .filter(Boolean);
  const specialistSpecialties = specs.filter((s, i, a) => a.indexOf(s) === i);

  const row = {
    alias,
    age: safeAge,
    sex: patient.sex || '',
    // Payer class (a coverage category, not PHI) so the model can apply the
    // prompt's PAYER CONSTRAINTS instead of assuming Medicare rules.
    payerClass: patient.payerClass || 'unknown',
    currentPrograms,
    specialistSpecialties,
    problemList,
    medications,
    derivedSignals: {
      conditionCount: (patient.conditions || []).length,
      medCount: medications.length,
    },
  };

  const mapEntry = {
    patientId: patient.id,
    displayName: ((patient.fname || '') + ' ' + (patient.lname || '')).trim(),
    existingPrograms: Object.assign({}, billing),
  };

  return { alias, row, mapEntry };
}

function _ageFromDob(dob) {
  if (!dob) return null;
  const t = Date.parse(dob);
  if (!Number.isFinite(t)) return null;
  const yrs = (Date.now() - t) / (365.2425 * 24 * 3600 * 1000);
  if (yrs < 0 || yrs > 130) return null;
  return Math.floor(yrs);
}

// Parses Claude's JSON response. Validates that aliases in the response are
// in the validAliases list (defense against the model hallucinating IDs).
// Throws Error on parse failure — orchestrator handles retry.
function parsePanelReviewResponse(responseText, validAliases) {
  let parsed;
  try {
    parsed = JSON.parse(responseText);
  } catch (e) {
    throw new Error('Panel review response is not valid JSON: ' + e.message);
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Panel review response is not an object');
  }
  if (!Array.isArray(parsed.patients)) {
    parsed.patients = [];
  }
  const allowed = new Set(validAliases);
  parsed.patients = parsed.patients.filter((p) => allowed.has(p && p.alias));
  return parsed;
}

// Splits an array into evenly sized chunks (last one may be smaller).
function chunkRows(rows, size) {
  const out = [];
  for (let i = 0; i < rows.length; i += size) {
    out.push(rows.slice(i, i + size));
  }
  return out;
}

module.exports = {
  suggestionHash,
  isCcmProgramCode,
  normalizeCcmSuggestionForDisposition,
  pickCcmSuggestion,
  pickBhiSuggestion,
  buildSafeHarborPanelRow,
  parsePanelReviewResponse,
  chunkRows,
};
